import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { Store } from '@ngxs/store';
import { TuiInputModule, TuiInputNumberModule } from '@taiga-ui/kit';
import { TuiButtonModule, TuiTextfieldControllerModule } from '@taiga-ui/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  catchError,
  combineLatest,
  filter,
  map,
  Subject,
  switchMap,
  tap,
  throwError,
  withLatestFrom,
} from 'rxjs';
import {
  countPalletsCartsAndBulks,
  getComputedProperties,
  getLoadingPercent,
  getRunningMetersFromCartQuantity,
  getRunningMetersFromUSPallets,
} from '@shared/utils/cargo-calculation';
import { CargoCatalogsService } from '@shared/services/cargo-catalogs.service';

import { AlertService } from '@shared/services/alert.service';
import { TuiActiveZoneModule } from '@taiga-ui/cdk';
import { CargoLoadingState } from '../../store/cargo-loading.state';
import { CommonStateService } from '../../services/common-state.service';
import { ComputedPropsForm } from '../../types';
import { Cargo, OrderCargoPlaceDetails } from '../../../../types/cargo';
import { CargoLoadingService } from '../../services/cargo-loading.service';
import { TransformToFormControl } from '../../../../types/common';

type CargoParamsForm = TransformToFormControl<
  Pick<Cargo, 'grossWeight' | 'temperatureRegime'>
>;
@Component({
  selector: 'app-cargo-characteristics',
  standalone: true,
  imports: [
    TuiInputModule,
    TuiTextfieldControllerModule,
    ReactiveFormsModule,
    TuiInputNumberModule,
    TuiButtonModule,
    TuiActiveZoneModule,
  ],
  templateUrl: './cargo-characteristics.component.html',
  styleUrl: './cargo-characteristics.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CargoCharacteristicsComponent implements OnInit {
  @ViewChild('container') container: ElementRef;
  computedProperties = this.fb.group<ComputedPropsForm>({
    totalColdPlaces: this.fb.control<string>({ value: '', disabled: true }),
    totalWarmPlaces: this.fb.control<string>({ value: '', disabled: true }),
    totalBox: this.fb.control<number>({ value: 0, disabled: true }),
  });

  zoneChanged$ = new Subject<boolean>();
  vehicleLoadingPercent = this.fb.control({ value: 0, disabled: true });

  form = this.fb.group<CargoParamsForm>({
    grossWeight: this.fb.control<Nullable<number>>(null),
    temperatureRegime: this.fb.control(''),
  });

  orders$ = this.store.select(CargoLoadingState.getOrders);
  cargo$ = this.store.select(CargoLoadingState.getSelectedCargo);
  trailerType$ = this.cargo$.pipe(map(cargo => cargo?.trailerType));

  constructor(
    private readonly fb: FormBuilder,
    private readonly store: Store,
    private readonly destroyRef: DestroyRef,
    protected readonly commonStateService: CommonStateService,
    private readonly cargoCatalogsService: CargoCatalogsService,
    private readonly alert: AlertService,
    private readonly cargoLoadingService: CargoLoadingService,
  ) {}

  updateComputedProperties() {
    this.orders$
      .pipe(
        tap(orders => {
          this.computedProperties.setValue({
            ...getComputedProperties(orders),
          });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  updateVehicleLoadingPercent() {
    combineLatest([this.orders$, this.trailerType$])
      .pipe(
        withLatestFrom(this.cargoCatalogsService.trailersTypes$),
        tap(([[preOrders, trailerType], trailerTypes]) => {
          if (!trailerType || !preOrders.length) {
            this.vehicleLoadingPercent.setValue(0);
          } else {
            const trailerTypeDetails = trailerTypes.find(
              trailer => trailer.code === trailerType,
            );

            const places: OrderCargoPlaceDetails[] = [];
            preOrders.forEach(preOrder => {
              places.push(...preOrder.cargoPlacesJson);
            });
            const [pallets, carts, bulk] = countPalletsCartsAndBulks(places);
            const palletsMeters = getRunningMetersFromUSPallets(pallets);
            const cartsMeters = getRunningMetersFromCartQuantity(carts);
            const meters = palletsMeters + cartsMeters + bulk;
            this.vehicleLoadingPercent.setValue(
              getLoadingPercent(meters, trailerTypeDetails?.length ?? 0),
            );
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  fillFormOnStateChange() {
    this.cargo$
      .pipe(
        tap(cargo => {
          this.form.patchValue(
            {
              grossWeight: cargo?.grossWeight,
              temperatureRegime: cargo?.temperatureRegime,
            },
            {
              emitEvent: false,
            },
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  updateOnChanges() {
    this.zoneChanged$
      .pipe(
        filter(focused => !focused && !this.form.pristine),
        withLatestFrom(this.cargo$),
        switchMap(([, cargo]) => {
          const payload: Cargo = {
            ...cargo,
            ...this.form.getRawValue(),
          } as Cargo;
          return this.cargoLoadingService.updateCargo(payload);
        }),
        tap(() => {
          this.form.markAsPristine();
          this.form.markAsUntouched();
          this.alert.showSuccess();
        }),
        catchError(err => {
          this.alert.showError(err.error?.error || 'Ошибка');
          return throwError(() => err);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  disableWhenReadonly() {
    this.cargoLoadingService.readonly$
      .pipe(
        tap(readonly => {
          if (readonly) {
            this.form.disable();
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  ngOnInit() {
    this.updateComputedProperties();
    this.updateVehicleLoadingPercent();
    this.fillFormOnStateChange();
    this.updateOnChanges();
    this.disableWhenReadonly();
    this.computedProperties.disable();
  }
}
